import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { usePathname } from "next/navigation";
import { useTenant } from "@/contexts/tenant-provider";
import { fetchClinicSetupState } from "@/api/clinic-setup";
const ROUTE_CATEGORY_MAP: Record<string, string[]> = {
  "/patients": ["patients"],
  "/calendar": ["appointments"],
  "/billing": ["billing"],
  "/settings/users": ["providers"],
  "/settings/services": ["services"],
  "/settings/products": ["products"],
  "/check-in/forms": ["forms"],
  "/memberships": ["memberships"],
  "/settings/medications": ["medications"],
  "/inventory": ["inventory"]
};
const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;
export interface ImportBannerInfo {
  sourceSystem: string | null;
  category: string;
  importCompletedAt: string;
}
export function useImportBannerInfo() {
  const $ = _c(17);
  const {
    tenant
  } = useTenant();
  const pathname = usePathname();
  const isSetupComplete = tenant?.clinicSetupComplete === true;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["clinic-setup", "state"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== isSetupComplete) {
    t1 = {
      queryKey: t0,
      queryFn: fetchClinicSetupState,
      enabled: isSetupComplete,
      staleTime: 300000
    };
    $[1] = isSetupComplete;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: setupState
  } = useQuery(t1);
  if (!isSetupComplete || !setupState) {
    return null;
  }
  let importCompletedAt;
  let meta;
  let t2;
  let t3;
  if ($[3] !== setupState.metadataJson) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      meta = setupState.metadataJson ?? {};
      importCompletedAt = meta.import_completed_at as string | undefined;
      if (!importCompletedAt) {
        t3 = null;
        break bb0;
      }
      t2 = new Date(importCompletedAt).getTime();
    }
    $[3] = setupState.metadataJson;
    $[4] = importCompletedAt;
    $[5] = meta;
    $[6] = t2;
    $[7] = t3;
  } else {
    importCompletedAt = $[4];
    meta = $[5];
    t2 = $[6];
    t3 = $[7];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  const completedDate = t2;
  if (Date.now() - completedDate > NINETY_DAYS_MS) {
    return null;
  }
  let t4;
  if ($[8] !== meta.categories) {
    t4 = meta.categories ?? {};
    $[8] = meta.categories;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const categories = t4 as Record<string, string>;
  let t5;
  if ($[10] !== categories) {
    t5 = Object.entries(categories).filter(_temp).map(_temp2);
    $[10] = categories;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const importedCategoryKeys = t5;
  if (importedCategoryKeys.length === 0) {
    return null;
  }
  let t6;
  if ($[12] !== importCompletedAt || $[13] !== importedCategoryKeys || $[14] !== meta.source_system || $[15] !== pathname) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb1: {
      const pathWithoutTenant = pathname.replace(/^\/[^/]+/, "");
      for (const [routePrefix, cats] of Object.entries(ROUTE_CATEGORY_MAP)) {
        if (pathWithoutTenant.startsWith(routePrefix)) {
          const matchedCategory = cats.find(c => importedCategoryKeys.includes(c));
          if (matchedCategory) {
            t6 = {
              sourceSystem: meta.source_system as string ?? null,
              category: matchedCategory,
              importCompletedAt
            };
            break bb1;
          }
        }
      }
    }
    $[12] = importCompletedAt;
    $[13] = importedCategoryKeys;
    $[14] = meta.source_system;
    $[15] = pathname;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  return null;
}
function _temp2(t0) {
  const [key] = t0;
  return key;
}
function _temp(t0) {
  const [, method] = t0;
  return method && method !== "skip";
}